/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Switch,
  Text,
  type UseDisclosureReturn,
  VStack,
  useDisclosure,
} from '@chakra-ui/react';
import type { OptionBase } from 'chakra-react-select';
import { t } from 'i18next';
import {
  type FieldArrayWithId,
  type RegisterOptions,
  type UseFieldArrayRemove,
  type UseFormReturn,
  useFormContext,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  FormField,
  FormFieldError,
  FormFieldItem,
  IconSettings,
} from '@/components';
import { Icon } from '@/components/Icons';
import {
  IconChange,
  IconDone,
  IconDotsVertical,
  IconTrash,
  IconTrashEmpty,
  IconUploadCloud,
} from '@/components/Icons/icons-generated/';
import { MarkdownEditor } from '@/components/MarkdownEditor/MarkdownEditor';
import { QuestionMediaModal } from '@/features/admin-new-interview/QuestionMediaModal';
import { InterviewType, QuestionType } from '@/services/api';

import { QuestionSettingModal } from '../admin-interview/QuestionSettingModal';
import { type EditInterviewQuestionsFormInputs } from '../admin-interview/schemas';
import { type NewInterviewFormInputs } from './common';

type InterviewQuestionCardProps = {
  field:
    | FieldArrayWithId<NewInterviewFormInputs, 'questions', 'id'>
    | FieldArrayWithId<EditInterviewQuestionsFormInputs, 'questions', 'id'>;
  index: number;
  fieldsLength: number;
  remove: UseFieldArrayRemove;
  isEditMode: boolean;
};

interface DurationOptions extends OptionBase {
  label: string;
  value: number;
}

interface QuestionTypeOptions extends OptionBase {
  label: string;
  value: QuestionType;
}

export const InterviewQuestionCard: React.FC<InterviewQuestionCardProps> = (
  props
) => {
  const { t } = useTranslation(['interview', 'common', 'candidate']);
  const { field, index, remove, fieldsLength } = props;
  const [isShowAudio, setIsShowAudio] = useState(false);
  const form = useFormContext<NewInterviewFormInputs>();
  const { getValues } = form;
  const imageUploadModal: UseDisclosureReturn = useDisclosure();

  const [settingsModalVisible, setSettingsModalVisible] =
    useState<boolean>(false);

  useEffect(() => {
    const isAudio =
      getValues(`questions.${index}.file`)?.type?.includes('audio') ??
      !!getValues(`questions.${index}.mediaFileKeys`)?.[0];
    setIsShowAudio(isAudio);
  }, [
    getValues(`questions.${index}.file`),
    getValues(`questions.${index}.mediaFileKeys`),
  ]);

  return (
    <>
      <Card
        position="relative"
        key={field.id}
        bgColor="gray.25"
        borderWidth={1}
        borderColor="gray.50"
        direction="column"
      >
        <CardBody p={6} display="flex" flexDir="column" rowGap={5}>
          <VStack>
            <QuestionFormLabel
              form={form}
              fieldsLength={fieldsLength}
              index={index}
              remove={remove}
            />

            <Flex w="full" columnGap={3}>
              <FormField
                type="custom"
                name={`questions.${index}.isCaseMode`}
                control={form.control}
                render={({ field: { value } }) => {
                  return value ? (
                    <CaseMode form={form} index={index} />
                  ) : (
                    <QuestionTitleInput form={form} index={index} />
                  );
                }}
              />
              <Button
                variant="unstyled"
                borderWidth={1}
                borderColor="gray.200"
                px={3.5}
                bg={
                  form.getValues(`questions.${index}.mediaFileKeys`) ||
                  form.getValues(`questions.${index}.imageFileKeys`)
                    ? 'gray.50'
                    : 'white'
                }
                onClick={() => imageUploadModal.onToggle()}
              >
                <IconUploadCloud fontSize={22} color="gray.500" />
              </Button>
            </Flex>
          </VStack>

          {isShowAudio && (
            <HStack>
              <audio controls>
                <source
                  src={
                    form.getValues(`questions.${index}.file`)
                      ? URL.createObjectURL(
                          form.getValues(`questions.${index}.file`)!
                        )
                      : getValues(`questions.${index}.mediaFileKeys`)?.[0]
                  }
                  type="audio/mpeg"
                />
              </audio>
              <Menu>
                <MenuButton
                  as={Button}
                  w={8}
                  rounded="full"
                  py={0}
                  px={0}
                  h={10}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  <IconDotsVertical />
                </MenuButton>
                <MenuList minWidth={36}>
                  <MenuItem
                    columnGap={2}
                    minWidth={36}
                    onClick={() => imageUploadModal.onToggle()}
                  >
                    <IconChange color="gray.500" fontSize={18} /> Değiştir
                  </MenuItem>
                  <MenuItem
                    color="red.600"
                    onClick={() => {
                      form.setValue(`questions.${index}.file`, undefined);
                      form.setValue(`questions.${index}.mediaFileKeys`, []);
                      setIsShowAudio(false);
                    }}
                    columnGap={2}
                    minWidth={36}
                  >
                    <IconTrash color="red.600" />
                    Sil
                  </MenuItem>
                </MenuList>
              </Menu>
            </HStack>
          )}

          <QuestionTypeAndDuration form={form} index={index} />

          {form.watch(`questions.${index}.questionType`) ===
            QuestionType.Code && (
            <HStack w="50%" pr={2}>
              <FormField
                type="select"
                label={t('common:language')}
                name={`questions.${index}.programmingLanguage`}
                options={PROGRAMMING_LANGUAGE.map((lang) => ({
                  label: lang,
                  value: lang,
                }))}
                rules={{
                  required: t(
                    'interview:createNewInterview.questionPreparation.responseTimeRequired'
                  ),
                }}
                control={form.control}
              />
            </HStack>
          )}

          <FormField
            type="custom"
            name={`questions.${index}.questionType`}
            control={form.control}
            render={({ field: { value } }) => {
              const qType = value;
              return (
                <>
                  {qType === QuestionType.MultipleSelection && (
                    <MultipleSelectionQuestion index={index} form={form} />
                  )}

                  {qType === QuestionType.MultipleChoice && (
                    <MultipleChoiceQuestion index={index} form={form} />
                  )}
                </>
              );
            }}
          />

          <Flex justifyContent="end" w="full">
            <Button
              onClick={() => setSettingsModalVisible(true)}
              variant="link"
            >
              <IconSettings fontSize={18} color="brand.500" />
              <Text color="primary.500" mx={2} fontWeight="600">
                {t('interview:createNewInterview.questionPreparation.settings')}
              </Text>
            </Button>
          </Flex>
        </CardBody>
      </Card>

      <QuestionMediaModal
        isOpen={imageUploadModal.isOpen}
        onClose={imageUploadModal.onToggle}
        form={form}
        index={index}
        isEditMode={props.isEditMode}
      />

      {settingsModalVisible && (
        <QuestionSettingModal
          isOpen={settingsModalVisible}
          onClose={() => setSettingsModalVisible(false)}
          questionType={form.getValues(`questions.${index}.questionType`)!}
          questionText={form.getValues(`questions.${index}.question`) ?? ''}
          initialValues={{
            skill: form.getValues(`questions.${index}.skill`) ?? undefined,
            thinkingTime: form.getValues(
              `questions.${index}.settings.thinkingTimeSeconds`
            ),
            questionWeight: form.getValues(
              `questions.${index}.settings.weight`
            ),
            answerAgain:
              form.getValues(`questions.${index}.settings.retryable`) ?? false,
            ignoreAiRating:
              form.getValues(`questions.${index}.settings.ignoreRating`) ??
              false,
            tags: form.getValues(`questions.${index}.tags`) ?? undefined,
            isAdminQuestion:
              form.getValues(`questions.${index}.settings.isAdminQuestion`) ??
              false,
          }}
          onSubmit={(data) => {
            form.setValue(`questions.${index}.skill`, data.skill);
            form.setValue(
              `questions.${index}.settings.thinkingTimeSeconds`,
              data.thinkingTime
            );
            form.setValue(`questions.${index}.tags`, data.tags);
            form.setValue(
              `questions.${index}.settings.weight`,
              data.questionWeight
            );
            form.setValue(
              `questions.${index}.settings.retryable`,
              data.answerAgain
            );
            form.setValue(
              `questions.${index}.settings.ignoreRating`,
              data.ignoreAiRating
            );
            form.setValue(
              `questions.${index}.settings.isAdminQuestion`,
              data.isAdminQuestion
            );
          }}
        />
      )}
    </>
  );
};

type CaseModeProps = {
  form: UseFormReturn<NewInterviewFormInputs, ExplicitAny, undefined>;
  index: number;
};

const CaseMode: React.FC<CaseModeProps> = (props) => {
  const { t } = useTranslation(['interview', 'common']);

  const questionInputRules: RegisterOptions<
    NewInterviewFormInputs,
    `questions.${number}.questionMarkdown`
  > = {
    required: t(
      'interview:createNewInterview.questionPreparation.questionRequired'
    ),
    validate: validateQuestionText,
  };

  return (
    <FormField
      control={props.form.control}
      name={`questions.${props.index}.questionMarkdown`}
      type="custom"
      rules={questionInputRules}
      render={({ field: { value, ref, onBlur, onChange, name } }) => {
        return (
          <FormFieldItem>
            <Box
              borderRadius="lg"
              border="1px"
              borderColor="gray.200"
              background="white"
              id={name}
            >
              <MarkdownEditor
                placeholder={t('interview:jobDescription.placeholder')}
                markdown={value ?? ''}
                onBlur={onBlur}
                ref={ref}
                onChange={onChange}
              />
            </Box>
            <FormFieldError />
          </FormFieldItem>
        );
      }}
    />
  );
};

type QuestionTitleInputProps = {
  index: number;
  form: UseFormReturn<NewInterviewFormInputs, ExplicitAny, undefined>;
};

const QuestionTitleInput: React.FC<QuestionTitleInputProps> = (props) => {
  const { index, form } = props;
  const { t } = useTranslation(['interview', 'common']);

  return (
    <FormField
      control={form.control}
      name={`questions.${index}.question`}
      type="text"
      placeholder={t(
        'interview:createNewInterview.questionPreparation.questionPlaceholder'
      )}
      rules={{
        required: t(
          'interview:createNewInterview.questionPreparation.questionRequired'
        ),
        validate: validateQuestionText,
      }}
    />
  );
};

type QuestionTypeAndDurationProps = {
  index: number;
  form: UseFormReturn<NewInterviewFormInputs, ExplicitAny, undefined>;
};

const QuestionTypeAndDuration: React.FC<QuestionTypeAndDurationProps> = (
  props
) => {
  const { index, form } = props;
  const { t } = useTranslation(['interview', 'common']);

  const toQuestionTypeOptions = useCallback(
    (type: QuestionType): QuestionTypeOptions => {
      return {
        label: t(
          `interview:createNewInterview.questionPreparation.questionTypeOptions.${type}`
        ),
        value: type,
      };
    },
    [t]
  );

  const [questionTypeOptions, setQuestionTypeOptions] = useState(
    Object.values(QuestionType)
      .filter((type) => type !== QuestionType.Code)
      .map(toQuestionTypeOptions)
  );

  const toDurationOptions = useCallback(
    (minute: number): DurationOptions => {
      return {
        label: t('common:timeAndDate.minute', { count: minute }),
        value: minute,
      };
    },
    [t]
  );

  // Show code question only in technical interview
  useEffect(() => {
    if (form.watch('type') === InterviewType.TechnicalInterview) {
      form.setValue(`questions.${index}.questionType`, QuestionType.Code);
      setQuestionTypeOptions([toQuestionTypeOptions(QuestionType.Code)]);
      return;
    }
    if (form.watch(`questions.${index}.questionType`) === QuestionType.Code) {
      form.setValue(`questions.${index}.questionType`, QuestionType.Video);
    }
    setQuestionTypeOptions(
      Object.values(QuestionType)
        .filter((type) => type !== QuestionType.Code)
        .map(toQuestionTypeOptions)
    );
  }, [form.watch('type')]);

  const durationOptions = useMemo(
    () => [
      ...Array.from(Array(10).keys())
        .map((x) => ++x)
        .map(toDurationOptions),
      toDurationOptions(15),
      toDurationOptions(20),
      toDurationOptions(30),
    ],
    [toDurationOptions]
  );

  return (
    <HStack>
      <FormField
        type="select"
        label={t(
          'interview:createNewInterview.questionPreparation.questionType'
        )}
        rules={{
          required: t(
            'interview:createNewInterview.questionPreparation.questionTypeIsRequired'
          ),
        }}
        name={`questions.${index}.questionType`}
        options={questionTypeOptions}
        control={form.control}
        onMenuClose={() => {
          if (
            ![
              QuestionType.MultipleSelection,
              QuestionType.MultipleChoice,
            ].includes(
              form.getValues(`questions.${index}.questionType`) as QuestionType
            )
          ) {
            form.setValue(`questions.${index}.settings.ignoreRating`, false);
          }
        }}
      />

      <FormField
        type="select"
        label={t(
          'interview:createNewInterview.questionPreparation.responseTime'
        )}
        name={`questions.${index}.durationMinutes`}
        options={durationOptions}
        rules={{
          required: t(
            'interview:createNewInterview.questionPreparation.responseTimeRequired'
          ),
        }}
        control={form.control}
      />
    </HStack>
  );
};

const validateQuestionText = (value: string | undefined) => {
  if (value && value?.trim().length < 10) {
    return t(
      'interview:createNewInterview.questionPreparation.questionMinLength'
    );
  }
};

type MultipleSelectionQuestionProps = {
  index: number;
  form: UseFormReturn<NewInterviewFormInputs, ExplicitAny, undefined>;
};

const MultipleSelectionQuestion: React.FC<MultipleSelectionQuestionProps> = ({
  index,
  form,
}) => {
  return (
    <HStack
      borderRadius="lg"
      borderColor="gray.100"
      pl={5}
      pr={1}
      py={3}
      bg="gray.50"
      borderWidth={1}
    >
      <FormField
        type="creatable-checkboxes"
        name={`questions.${index}.choices`}
        isCheckBoxDisabled
        selection="multiple"
        options={[
          {
            value: 'option1',
            label:
              '1. ' +
              t('interview:createNewInterview.questionPreparation.option'),
            checked: false,
          },
          {
            value: 'option2',
            label:
              '2. ' +
              t('interview:createNewInterview.questionPreparation.option'),
            checked: false,
          },
        ]}
        direction="column"
        size="md"
        control={form.control}
      />
    </HStack>
  );
};

const MultipleChoiceQuestion: React.FC<MultipleSelectionQuestionProps> = ({
  index,
  form,
}) => {
  return (
    <HStack
      borderRadius="lg"
      borderColor="gray.100"
      pl={5}
      pr={1}
      py={3}
      bg="gray.50"
      borderWidth={1}
    >
      <FormField
        type="creatable-checkboxes"
        name={`questions.${index}.choices`}
        selection="single"
        options={[
          {
            value: 'option1',
            label:
              '1. ' +
              t('interview:createNewInterview.questionPreparation.option'),
            checked: false,
          },
          {
            value: 'option2',
            label:
              '2. ' +
              t('interview:createNewInterview.questionPreparation.option'),
            checked: false,
          },
        ]}
        isRequired
        direction="column"
        size="md"
        control={form.control}
      />
    </HStack>
  );
};

type QuestionFormLabelProps = {
  fieldsLength: number;
  index: number;
  remove: UseFieldArrayRemove;
  form: UseFormReturn<NewInterviewFormInputs, ExplicitAny, undefined>;
};

const QuestionFormLabel: React.FC<QuestionFormLabelProps> = (props) => {
  const { index, fieldsLength, remove, form } = props;
  return (
    <Flex justifyContent="space-between" w="full">
      <Flex columnGap={3}>
        <Text>{`${index + 1}. ${t('common:detail.question')}`} </Text>
        {form.getValues(`questions.${index}.isAiGenerated`) ? (
          <Flex
            alignItems="center"
            columnGap="1"
            px={2}
            bg="primary.50"
            borderRadius="full"
          >
            <IconDone color="primary.700" />
            <Text
              whiteSpace="nowrap"
              fontSize="12px"
              fontWeight="semibold"
              color="primary.700"
            >
              {t('common:aiGenerated')}
            </Text>
          </Flex>
        ) : (
          ''
        )}
      </Flex>

      <Flex columnGap={2}>
        {fieldsLength > 1 && (
          <Button
            textColor="gray.500"
            variant="link"
            onClick={() => remove(index)}
          >
            <Flex align="center" gap={2}>
              <Icon fontSize="lg" icon={IconTrashEmpty as ExplicitAny} />
            </Flex>
          </Button>
        )}

        <FormField
          type="custom"
          control={form.control}
          name={`questions.${index}.isCaseMode`}
          render={({ field: { value, ...rest } }) => {
            return (
              <Flex fontWeight="medium" align="center" gap={2}>
                <Switch
                  {...rest}
                  isChecked={value}
                  colorScheme="brand"
                  size="md"
                />
                <Text>Case mode</Text>
              </Flex>
            );
          }}
        />
      </Flex>
    </Flex>
  );
};

const PROGRAMMING_LANGUAGE = [
  'Python',
  'Javascript',
  'Java',
  'C',
  'C++',
  'C#',
  'Ruby',
  'PHP',
  'SQL',
  'React Native',
  'Swift',
];
